'use client';

import React, { useEffect, useRef, useState } from 'react';
import { SUPPORTED_LANGUAGES, getTranslations, type Language } from '@/lib/i18n';

const PHONE = '+38631004495';
const PHONE_CLEAN = '38631004495';
const APP_URL = 'https://navigacija.easydrive.app';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  bgColor: string;
}

interface Step {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

function useIntersectionObserver(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

function AnimatedSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useIntersectionObserver();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
    >
      {children}
    </div>
  );
}

function LanguageSwitcher({ currentLang, onChangeLang }: { currentLang: Language; onChangeLang: (lang: Language) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
        style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)', color: '#22d3ee' }}
        aria-label="Select language"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
          <path d="M2 12h20"/>
        </svg>
        <span>{currentLang.code.toUpperCase()}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50 rounded-xl overflow-hidden"
          style={{ background: '#0f172a', border: '1px solid rgba(34,211,238,0.15)', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', minWidth: '180px', maxHeight: '320px', overflowY: 'auto' }}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => { onChangeLang(lang); setOpen(false); }}
              className="w-full flex items-center justify-between px-4 py-2.5 text-left text-xs transition-colors"
              style={{
                color: lang.code === currentLang.code ? '#22d3ee' : '#94a3b8',
                background: lang.code === currentLang.code ? 'rgba(34,211,238,0.08)' : 'transparent',
              }}
              onMouseEnter={e => { if (lang.code !== currentLang.code) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { if (lang.code !== currentLang.code) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <span className="font-medium">{lang.nativeName}</span>
              <span style={{ color: '#475569' }}>{lang.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState<Language>(SUPPORTED_LANGUAGES[0]);

  const t = getTranslations(currentLang.code);
  const isRTL = currentLang.dir === 'rtl';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features: Feature[] = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
          <circle cx="12" cy="9" r="2.5"/>
        </svg>
      ),
      title: t.features.items.liveGps.title,
      description: t.features.items.liveGps.description,
      color: 'text-ed-cyan',
      bgColor: 'bg-ed-cyan/10',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2"/>
          <path d="M8 21h8"/>
          <path d="M12 17v4"/>
        </svg>
      ),
      title: t.features.items.webApp.title,
      description: t.features.items.webApp.description,
      color: 'text-ed-green',
      bgColor: 'bg-ed-green/10',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      title: t.features.items.traccar.title,
      description: t.features.items.traccar.description,
      color: 'text-ed-purple',
      bgColor: 'bg-purple-500/10',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      title: t.features.items.updates.title,
      description: t.features.items.updates.description,
      color: 'text-ed-warning',
      bgColor: 'bg-yellow-500/10',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      ),
      title: t.features.items.profiles.title,
      description: t.features.items.profiles.description,
      color: 'text-ed-cyan',
      bgColor: 'bg-ed-cyan/10',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
        </svg>
      ),
      title: t.features.items.radio.title,
      description: t.features.items.radio.description,
      color: 'text-ed-green',
      bgColor: 'bg-ed-green/10',
    },
  ];

  const steps: Step[] = [
    {
      number: '01',
      title: t.howItWorks.steps.open.title,
      description: t.howItWorks.steps.open.description,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
          <path d="M2 12h20"/>
        </svg>
      ),
    },
    {
      number: '02',
      title: t.howItWorks.steps.profile.title,
      description: t.howItWorks.steps.profile.description,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      ),
    },
    {
      number: '03',
      title: t.howItWorks.steps.server.title,
      description: t.howItWorks.steps.server.description,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
          <line x1="6" y1="6" x2="6.01" y2="6"/>
          <line x1="6" y1="18" x2="6.01" y2="18"/>
        </svg>
      ),
    },
    {
      number: '04',
      title: t.howItWorks.steps.track.title,
      description: t.howItWorks.steps.track.description,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="5 3 19 12 5 21 5 3"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0f1e', color: '#e2e8f0', fontFamily: "'DM Sans', sans-serif" }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'nav-blur' : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.3)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
                  <circle cx="7" cy="17" r="2"/>
                  <path d="M9 17h6"/>
                  <circle cx="17" cy="17" r="2"/>
                </svg>
              </div>
              <span className="text-white font-bold text-lg tracking-tight">EasyDrive</span>
            </div>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm transition-colors" style={{ color: '#64748b' }} onMouseEnter={e => (e.currentTarget.style.color = '#22d3ee')} onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}>{t.nav.features}</a>
              <a href="#how-it-works" className="text-sm transition-colors" style={{ color: '#64748b' }} onMouseEnter={e => (e.currentTarget.style.color = '#22d3ee')} onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}>{t.nav.howItWorks}</a>
              <a href="#contact" className="text-sm transition-colors" style={{ color: '#64748b' }} onMouseEnter={e => (e.currentTarget.style.color = '#22d3ee')} onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}>{t.nav.contact}</a>
            </div>

            {/* CTA Button + Language Switcher */}
            <div className="flex items-center gap-3">
              <LanguageSwitcher currentLang={currentLang} onChangeLang={setCurrentLang} />
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
                {t.nav.openApp}
              </a>
              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 rounded-lg"
                style={{ color: '#64748b' }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {mobileMenuOpen ? (
                    <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                  ) : (
                    <><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></>
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t" style={{ borderColor: 'rgba(34,211,238,0.1)' }}>
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-sm" style={{ color: '#94a3b8' }} onClick={() => setMobileMenuOpen(false)}>{t.nav.features}</a>
                <a href="#how-it-works" className="text-sm" style={{ color: '#94a3b8' }} onClick={() => setMobileMenuOpen(false)}>{t.nav.howItWorks}</a>
                <a href="#contact" className="text-sm" style={{ color: '#94a3b8' }} onClick={() => setMobileMenuOpen(false)}>{t.nav.contact}</a>
                <a
                  href={APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t.nav.openApp}
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient bg-grid relative overflow-hidden pt-24 pb-20 md:pt-32 md:pb-28">
        <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: 'radial-gradient(circle, #22d3ee, transparent)' }} />
        <div className="absolute bottom-10 right-1/4 w-80 h-80 rounded-full opacity-8 blur-3xl" style={{ background: 'radial-gradient(circle, #10b981, transparent)' }} />
        <div className="absolute top-1/2 left-10 w-64 h-64 rounded-full opacity-6 blur-3xl" style={{ background: 'radial-gradient(circle, #a855f7, transparent)' }} />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-xs font-semibold tracking-wider uppercase" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)', color: '#22d3ee' }}>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#22d3ee' }}></span>
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: '#22d3ee' }}></span>
              </span>
              {t.hero.badge}
            </div>

            {/* App Icon */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="w-24 h-24 rounded-3xl flex items-center justify-center animate-float" style={{ background: 'rgba(34,211,238,0.12)', border: '1px solid rgba(34,211,238,0.25)', boxShadow: '0 0 40px rgba(34,211,238,0.2)' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
                    <circle cx="7" cy="17" r="2"/>
                    <path d="M9 17h6"/>
                    <circle cx="17" cy="17" r="2"/>
                  </svg>
                </div>
                <div className="absolute inset-0 rounded-3xl animate-ping" style={{ border: '1px solid rgba(34,211,238,0.3)', animationDuration: '2s' }} />
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 leading-tight">
              <span className="text-white">EasyDrive</span>
              <br />
              <span className="text-gradient-cyan">{t.hero.tagline}</span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl mb-4 font-medium" style={{ color: '#94a3b8' }}>
              {t.hero.subtitle}
            </p>

            <p className="text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: '#64748b' }}>
              {t.hero.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-bold w-full sm:w-auto justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
                {t.hero.openWebApp}
              </a>
              <a
                href="#features"
                className="btn-outline inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-semibold w-full sm:w-auto justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {t.hero.learnMore}
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
              {[
                { value: '10s', label: t.hero.stats.updateInterval },
                { value: '100%', label: t.hero.stats.webBased },
                { value: '24/7', label: t.hero.stats.liveTracking },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 rounded-2xl" style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(34,211,238,0.1)' }}>
                  <div className="text-2xl font-bold text-gradient-cyan">{stat.value}</div>
                  <div className="text-xs mt-1" style={{ color: '#64748b' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs" style={{ color: '#64748b' }}>{t.hero.scroll}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14"/>
            <path d="m19 12-7 7-7-7"/>
          </svg>
        </div>
      </section>

      {/* App Preview Section */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #0a0f1e 0%, #0f172a 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t.preview.title}</h2>
              <p className="text-lg" style={{ color: '#64748b' }}>{t.preview.subtitle}</p>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="relative max-w-sm mx-auto">
              <div className="relative rounded-[2.5rem] overflow-hidden" style={{ background: '#0f172a', border: '2px solid rgba(34,211,238,0.2)', boxShadow: '0 40px 80px rgba(0,0,0,0.5), 0 0 60px rgba(34,211,238,0.1)' }}>
                {/* Status bar */}
                <div className="flex items-center justify-between px-6 py-3" style={{ background: '#0a0f1e' }}>
                  <span className="text-xs font-medium" style={{ color: '#64748b' }}>9:41</span>
                  <div className="flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="#64748b">
                      <path d="M1.5 8.5a13 13 0 0 1 21 0M5 12a10 10 0 0 1 14 0M8.5 15.5a6 6 0 0 1 7 0M12 19h.01" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="11" rx="2" ry="2"/>
                      <path d="M22 11v3"/>
                    </svg>
                  </div>
                </div>

                {/* App content */}
                <div className="px-6 py-8" style={{ background: '#0a0f1e' }}>
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <h3 className="text-white font-bold text-lg">EasyDrive</h3>
                      <p className="text-xs" style={{ color: '#64748b' }}>GPS Tracker</p>
                    </div>
                    <div className="flex items-center gap-1 px-3 py-1.5 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#10b981' }}></span>
                        <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: '#10b981' }}></span>
                      </span>
                      <span className="text-xs font-semibold" style={{ color: '#10b981' }}>LIVE</span>
                    </div>
                  </div>

                  {/* Map placeholder */}
                  <div className="rounded-2xl mb-6 overflow-hidden relative" style={{ height: '180px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', border: '1px solid rgba(34,211,238,0.15)' }}>
                    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(rgba(34,211,238,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.3) 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="relative">
                        <div className="w-4 h-4 rounded-full" style={{ background: '#22d3ee', boxShadow: '0 0 0 4px rgba(34,211,238,0.3)' }} />
                        <div className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(34,211,238,0.4)' }} />
                      </div>
                    </div>
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 180" preserveAspectRatio="none">
                      <path d="M 20 160 Q 80 120 120 90 T 180 90" stroke="rgba(34,211,238,0.4)" strokeWidth="2" fill="none" strokeDasharray="5,5" />
                    </svg>
                    <div className="absolute bottom-3 right-3 text-xs font-mono" style={{ color: '#22d3ee' }}>46.0569° N, 14.5058° E</div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    {[
                      { label: 'Speed', value: '62 km/h', icon: '⚡' },
                      { label: 'Accuracy', value: '±3m', icon: '🎯' },
                      { label: 'Sent', value: '1,247', icon: '📡' },
                    ].map((item) => (
                      <div key={item.label} className="text-center p-3 rounded-xl" style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(34,211,238,0.1)' }}>
                        <div className="text-base mb-1">{item.icon}</div>
                        <div className="text-xs font-bold text-white">{item.value}</div>
                        <div className="text-xs" style={{ color: '#64748b' }}>{item.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Start button */}
                  <button className="w-full py-4 rounded-2xl font-bold text-sm" style={{ background: 'linear-gradient(135deg, #22d3ee 0%, #10b981 100%)', color: '#0a0f1e' }}>
                    ● TRACKING ACTIVE
                  </button>
                </div>

                {/* Bottom nav */}
                <div className="flex items-center justify-around py-3 px-4" style={{ background: '#0f172a', borderTop: '1px solid rgba(34,211,238,0.1)' }}>
                  {[
                    { label: 'LIVE', active: true },
                    { label: 'Profile', active: false },
                    { label: 'Radio', active: false },
                    { label: 'EN', active: false },
                  ].map((item) => (
                    <div key={item.label} className="flex flex-col items-center gap-1">
                      <div className="w-5 h-5 rounded" style={{ background: item.active ? 'rgba(34,211,238,0.2)' : 'transparent' }} />
                      <span className="text-xs font-medium" style={{ color: item.active ? '#22d3ee' : '#64748b' }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute -inset-4 rounded-[3rem] opacity-20 blur-2xl -z-10" style={{ background: 'radial-gradient(ellipse, #22d3ee, transparent)' }} />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20" style={{ background: '#0a0f1e' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-xs font-semibold tracking-wider uppercase" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)', color: '#22d3ee' }}>
                {t.features.badge}
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                {t.features.title}
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: '#64748b' }}>
                {t.features.subtitle}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <AnimatedSection key={index}>
                <div
                  className="feature-card h-full p-6 rounded-2xl"
                  style={{
                    background: '#0f172a',
                    border: '1px solid rgba(30,41,59,1)',
                    transitionDelay: `${index * 50}ms`,
                  }}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${feature.bgColor}`}>
                    <span className={feature.color}>{feature.icon}</span>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#64748b' }}>{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20" style={{ background: 'linear-gradient(180deg, #0a0f1e 0%, #0f172a 50%, #0a0f1e 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-xs font-semibold tracking-wider uppercase" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10b981' }}>
                {t.howItWorks.badge}
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                {t.howItWorks.title}
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: '#64748b' }}>
                {t.howItWorks.subtitle}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {steps.map((step, index) => (
              <AnimatedSection key={step.number}>
                <div
                  className="flex gap-5 p-6 rounded-2xl"
                  style={{
                    background: '#0f172a',
                    border: '1px solid rgba(30,41,59,1)',
                    transitionDelay: `${index * 100}ms`,
                  }}
                >
                  <div className="shrink-0">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center font-mono font-bold text-sm" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)', color: '#22d3ee' }}>
                      {step.number}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ color: '#22d3ee' }}>{step.icon}</span>
                      <h3 className="text-white font-bold text-lg">{step.title}</h3>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: '#64748b' }}>{step.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div className="text-center mt-12">
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-bold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
                {t.howItWorks.tryNow}
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Web App Section */}
      <section className="py-20" style={{ background: '#0a0f1e' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-xs font-semibold tracking-wider uppercase" style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', color: '#a855f7' }}>
                  {t.webOnly.badge}
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                  {t.webOnly.title}<br />
                  <span className="text-gradient-cyan">{t.webOnly.titleHighlight}</span>
                </h2>
                <p className="text-lg mb-8 leading-relaxed" style={{ color: '#64748b' }}>
                  {t.webOnly.description}
                </p>
                <div className="space-y-4">
                  {t.webOnly.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-sm font-bold mt-0.5" style={{ color: '#10b981' }}>✓</span>
                      <span className="text-sm" style={{ color: '#94a3b8' }}>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: '🌐', title: t.webOnly.cards.browser.title, desc: t.webOnly.cards.browser.desc },
                  { icon: '📱', title: t.webOnly.cards.mobile.title, desc: t.webOnly.cards.mobile.desc },
                  { icon: '⚡', title: t.webOnly.cards.instant.title, desc: t.webOnly.cards.instant.desc },
                  { icon: '🔒', title: t.webOnly.cards.secure.title, desc: t.webOnly.cards.secure.desc },
                ].map((card) => (
                  <div
                    key={card.title}
                    className="p-5 rounded-2xl"
                    style={{ background: '#0f172a', border: '1px solid rgba(30,41,59,1)' }}
                  >
                    <div className="text-3xl mb-3">{card.icon}</div>
                    <h4 className="text-white font-semibold text-sm mb-1">{card.title}</h4>
                    <p className="text-xs leading-relaxed" style={{ color: '#64748b' }}>{card.desc}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Traccar Integration Section */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #0a0f1e 0%, #0f172a 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="rounded-3xl p-8 md:p-12 text-center" style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(34,211,238,0.15)', boxShadow: '0 0 60px rgba(34,211,238,0.05)' }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                  <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                  <line x1="6" y1="6" x2="6.01" y2="6"/>
                  <line x1="6" y1="18" x2="6.01" y2="18"/>
                </svg>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {t.traccarSection.title}
              </h2>
              <p className="text-lg max-w-2xl mx-auto mb-8" style={{ color: '#64748b' }}>
                {t.traccarSection.description}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.15)' }}>
                  <span style={{ color: '#22d3ee' }}>⚡</span>
                  <span className="text-sm font-medium" style={{ color: '#94a3b8' }}>{t.traccarSection.tags.osmand}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.15)' }}>
                  <span style={{ color: '#22d3ee' }}>🗺️</span>
                  <span className="text-sm font-medium" style={{ color: '#94a3b8' }}>{t.traccarSection.tags.traccar}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.15)' }}>
                  <span style={{ color: '#22d3ee' }}>📡</span>
                  <span className="text-sm font-medium" style={{ color: '#94a3b8' }}>{t.traccarSection.tags.realtime}</span>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20" style={{ background: '#0a0f1e' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-xs font-semibold tracking-wider uppercase" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)', color: '#22d3ee' }}>
                {t.contact.badge}
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                {t.contact.title}
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: '#64748b' }}>
                {t.contact.description}
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="max-w-2xl mx-auto">
              {/* Phone number display */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl" style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(34,211,238,0.15)' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.69h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l.77-.77a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 17.18z"/>
                  </svg>
                  <span className="font-mono font-bold text-lg" style={{ color: '#e2e8f0' }}>{PHONE}</span>
                </div>
              </div>

              {/* Messaging buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* WhatsApp */}
                <a
                  href={`https://wa.me/${PHONE_CLEAN}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn flex flex-col items-center gap-3 p-6 rounded-2xl text-white font-semibold"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                  </svg>
                  <div className="text-center">
                    <div className="font-bold text-base">{t.contact.whatsapp}</div>
                    <div className="text-xs opacity-80 mt-0.5">{t.contact.whatsappSub}</div>
                  </div>
                </a>
              </div>

              {/* Additional contact info */}
              <div className="mt-8 text-center">
                <p className="text-sm" style={{ color: '#64748b' }}>
                  {t.contact.availability}
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #0a0f1e 0%, #0f172a 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <div className="rounded-3xl p-10 md:p-16 relative overflow-hidden" style={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(34,211,238,0.2)' }}>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 opacity-20 blur-3xl" style={{ background: 'radial-gradient(ellipse, #22d3ee, transparent)' }} />

              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-float" style={{ background: 'rgba(34,211,238,0.12)', border: '1px solid rgba(34,211,238,0.25)' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
                    <circle cx="7" cy="17" r="2"/>
                    <path d="M9 17h6"/>
                    <circle cx="17" cy="17" r="2"/>
                  </svg>
                </div>

                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                  {t.cta.title}
                </h2>
                <p className="text-lg mb-8 max-w-xl mx-auto" style={{ color: '#64748b' }}>
                  {t.cta.description}
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href={APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-bold w-full sm:w-auto justify-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                    {t.cta.openApp}
                  </a>
                  <a
                    href="#contact"
                    className="btn-outline inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-semibold w-full sm:w-auto justify-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.69h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l.77-.77a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 17.18z"/>
                    </svg>
                    {t.cta.contactSales}
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12" style={{ background: '#0f172a', borderTop: '1px solid rgba(30,41,59,1)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.3)' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
                    <circle cx="7" cy="17" r="2"/>
                    <path d="M9 17h6"/>
                    <circle cx="17" cy="17" r="2"/>
                  </svg>
                </div>
                <span className="text-white font-bold text-lg">EasyDrive</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#64748b' }}>
                {t.footer.description}
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">{t.footer.quickLinks}</h4>
              <div className="space-y-2">
                {[
                  { label: t.footer.links.openApp, href: APP_URL, external: true },
                  { label: t.footer.links.features, href: '#features', external: false },
                  { label: t.footer.links.howItWorks, href: '#how-it-works', external: false },
                  { label: t.footer.links.contact, href: '#contact', external: false },
                ].map((link) => (
                  <div key={link.label}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      className="text-sm transition-colors"
                      style={{ color: '#64748b' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#22d3ee')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}
                    >
                      {link.label}
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">{t.footer.contactTitle}</h4>
              <div className="space-y-3">
                <a
                  href={`https://wa.me/${PHONE_CLEAN}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm transition-colors"
                  style={{ color: '#64748b' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#25D366')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </a>
                <div className="flex items-center gap-2 text-sm" style={{ color: '#64748b' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.69h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l.77-.77a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 17.18z"/>
                  </svg>
                  {PHONE}
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8" style={{ borderTop: '1px solid rgba(30,41,59,1)' }}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm" style={{ color: '#64748b' }}>
                © {new Date().getFullYear()} {t.footer.copyright}
              </p>
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#10b981' }}></span>
                  <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: '#10b981' }}></span>
                </span>
                <span className="text-xs" style={{ color: '#64748b' }}>{t.footer.serviceOnline}</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating contact buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        <a
          href={`https://wa.me/${PHONE_CLEAN}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg"
          style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', boxShadow: '0 4px 15px rgba(37,211,102,0.4)' }}
          title="WhatsApp"
          aria-label="Contact via WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
          </svg>
        </a>
      </div>
    </div>
  );
}
